/**
 * FilmStoragePage — DTF Film Storage dashboard (Phase 50.01.4 / BLU-233).
 *
 * Two storage MODES (Track B — two-mode film storage):
 *   - pigeon_hole — a per-ORDER, single-film hole. Order-bound, capacity 1. The
 *     grid clusters occupied holes UNDER their order (a bordered group headed by
 *     the order #); a 1-hole order is just a 1-cell cluster. Empty/unbound holes
 *     collect in a trailing "Available" cluster.
 *   - shelf (+ legacy rack/drawer/folder) — bulk, order-blind storage; rendered
 *     as the classic square occupancy grid below the holes.
 *
 * Two surfaces, ONE scanner input:
 *   (a) DIRECTED STOW — scan a cut transfer's garment QR → the backend auto-picks
 *       a slot and a big directive (the shared FilmLocationDirective) tells the
 *       operator "FILE IN HOLE-07 → ORDER #1234"; the scanner re-arms. An
 *       already-filed film re-directs to its slot (idempotent).
 *   (b) FULL-SCREEN INSPECT — tap any cell OR scan a pigeon-hole QR
 *       (THP://HOLE:<label>) → a full-screen overlay (mirrors BinManagePage's
 *       bin detail) shows the slot's retrieval directive, the bound order, and
 *       the film list (image, product, size/colour, sku, film status).
 *   The one scanner routes by QR prefix: THP://HOLE: → inspect, else → stow.
 *
 * Mirrors CatcherPage scanner wiring (QRScannerInput + useScannerAutoFocus +
 * useScanFeedback + role="status" aria-live); scan errors are operator-blocking
 * modals + audio. Plus an "Add Storage" creation flow (coordinator/admin —
 * gated server-side).
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { QRScannerInput } from '@/features/catcher-station';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { trpc } from '@/lib/trpc';
import { extractGarmentId, extractFilmSlotLabel } from '@/lib/qr-utils';
import { getUserMessage } from '@/lib/trpc-error-utils';
import { toast } from 'sonner';
import { Archive, Check, CheckCircle2, Circle, FolderOpen, ImageOff, Inbox, Layers, Loader2, Package, Pencil, Plus, Printer, ScanLine, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRole } from '@/hooks/use-role';
import { FloatingSelectionBar } from '@/components/layout/FloatingSelectionBar';
import { QRPreviewDialog } from '@/features/production-settings/components/QRPrintLayout';
import { FilmRedoDialog } from '@/components/production/FilmRedoDialog';
import { playSuccessBeep } from '@/lib/sounds';
import { FilmLocationDirective } from './FilmLocationDirective';
import { ZoomImg } from '@/components/production/HoverZoom';

/** The two creatable modes (legacy rack/drawer/folder rows stay readable only). */
type StorageType = 'pigeon_hole' | 'shelf';

/**
 * One storage slot as rendered by the grid. Structurally satisfied by the
 * backend FilmStorageRecord (storageType/status unions widen to string here).
 */
interface FilmSlot {
  id: string;
  label: string;
  storageType: string;
  status: string;
  currentCount: number;
  capacity: number | null;
  designGroup: string | null;
  currentOrderId: string | null;
  currentOrderName: string | null;
  currentOrderNumber: string | null;
  /** A DEFECTIVE film still sits in this slot → red "needs attention" cell. */
  hasDefectiveFilm?: boolean;
}

/**
 * Visual state of a slot cell. Occupancy colours (design-sprint lock):
 * green = has film(s), grey = empty, red = ONLY when a defective film needs
 * pulling. A full slot is a NORMAL state — never red (PRODUCTION_DESIGN §4.1
 * reserves red for problems).
 */
type SlotVisual = 'OCCUPIED' | 'EMPTY' | 'DEFECTIVE';
const slotVisual = (slot: FilmSlot): SlotVisual => {
  if (slot.hasDefectiveFilm) return 'DEFECTIVE';
  return slot.currentCount > 0 ? 'OCCUPIED' : 'EMPTY';
};

/** The directed-stow result: which slot the operator should file the film into. */
interface StowDirectiveState {
  slotLabel: string;
  storageType: string;
  orderName: string | null;
  orderNumber: string | null;
  /** true = the film was already in this slot (re-scan) — an FYI, not an action. */
  alreadyStored: boolean;
}

/** Full-screen inspect target — a cell tap carries the id, a scan the label. */
type InspectTarget = { storageId?: string; label?: string };

/**
 * Edit-mode selection controller threaded into the slot cells. null = normal
 * (inspect) mode; non-null = edit mode, where a cell tap toggles selection
 * instead of opening inspect.
 */
type SlotSelection = { selectedIds: Set<string>; onToggle: (id: string) => void } | null;

export function FilmStoragePage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const feedback = useScanFeedback();
  const utils = trpc.useUtils();

  const [showCreate, setShowCreate] = useState(false);
  const [directive, setDirective] = useState<StowDirectiveState | null>(null);
  const [inspect, setInspect] = useState<InspectTarget | null>(null);

  // Edit-storage (bulk remove) state — admin-only affordance.
  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [showPrintLabels, setShowPrintLabels] = useState(false);

  const { isAdmin, isWarehouseCoordinator } = useRole();
  // Print labels + create are coordinator/admin (setup actions, gated server-side);
  // remove/edit is admin-only (matches the DELETE RLS is_admin() + bins precedent).
  const canManageStorage = isAdmin() || isWarehouseCoordinator();

  const stowMutation = trpc.filmStorage.stowFilm.useMutation();
  const deleteMutation = trpc.filmStorage.deleteFilmStorage.useMutation();

  const hasScannedRef = useRef(false);
  const shownErrorRef = useRef<string | null>(null);

  const storageQuery = trpc.filmStorage.getFilmStorage.useQuery(
    { locationId: selectedLocation?.id ?? '' },
    { enabled: !!selectedLocation?.id, staleTime: 15_000 },
  );

  // Slot-label QR sheet — lazy, only fetched while the print dialog is open.
  const slotQRQuery = trpc.filmStorage.generateFilmSlotQRs.useQuery(
    { locationId: selectedLocation?.id ?? '' },
    { enabled: showPrintLabels && !!selectedLocation?.id },
  );

  // Pause scanner-focus while ANY blocking overlay is open OR we're editing
  // storage (a management mode, not a scan flow — the scanner must stand down).
  const scannerBlocked =
    feedback.isModalOpen || showCreate || inspect !== null || editMode || showPrintLabels || showRemoveConfirm;
  useScannerAutoFocus({ paused: scannerBlocked });

  // Reset on location change (incl. any in-progress edit selection).
  useEffect(() => {
    setDirective(null);
    setInspect(null);
    setEditMode(false);
    setSelectedIds(new Set());
    hasScannedRef.current = false;
    shownErrorRef.current = null;
  }, [selectedLocation?.id]);

  // Show error modal on storage-load failure (feedback excluded from deps).
  useEffect(() => {
    const err = storageQuery.error?.message ?? null;
    if (err && shownErrorRef.current !== err) {
      shownErrorRef.current = err;
      feedback.triggerError(t('filmStorage.loadError'), getUserMessage(storageQuery.error));
    } else if (!err) {
      shownErrorRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- feedback recreated every render
  }, [storageQuery.error, t]);

  const openInspect = useCallback((slot: FilmSlot) => {
    setInspect({ storageId: slot.id });
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const exitEditMode = useCallback(() => {
    setEditMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleRemove = useCallback(async () => {
    const ids = [...selectedIds];
    if (ids.length === 0 || !selectedLocation?.id) return;
    try {
      const res = await deleteMutation.mutateAsync({ locationId: selectedLocation.id, storageIds: ids });
      // Occupied / order-bound slots are skipped server-side — surface that so the
      // operator knows why a slot they selected is still there.
      if (res.skippedSlots.length > 0) {
        toast.warning(res.message, { description: t('filmStorage.removeSkippedDesc', { count: res.skippedSlots.length }) });
      } else {
        toast.success(res.message);
      }
      setSelectedIds(new Set());
      setShowRemoveConfirm(false);
      void utils.filmStorage.getFilmStorage.invalidate();
    } catch (error) {
      toast.error(t('filmStorage.removeFailed'), { description: getUserMessage(error) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- utils/deleteMutation stable; t recreated per render
  }, [selectedIds, selectedLocation?.id]);

  // ONE scanner input, routed by QR prefix:
  //   - THP://HOLE:<label> → open the full-screen inspect overlay for that hole.
  //   - anything else       → directed stow (scan a cut transfer's garment QR →
  //     the backend picks a slot; an already-filed film re-directs to its slot).
  const handleScan = useCallback(async (qrCode: string) => {
    hasScannedRef.current = true;
    if (!selectedLocation?.id) return;

    // Route: a film-storage slot QR (THP://FILMPH:, or legacy THP://HOLE:)
    // opens inspect (no stow mutation).
    const slotLabel = extractFilmSlotLabel(qrCode);
    if (slotLabel) {
      setDirective(null);
      setInspect({ label: slotLabel });
      return;
    }

    const garmentId = extractGarmentId(qrCode);
    if (!garmentId) {
      feedback.triggerError(t('filmStorage.scanError'), t('filmStorage.scanErrorDesc'));
      return;
    }
    // Clear the previous directive immediately so the prior film's directive can
    // never be misread as THIS scan's slot while the mutation is in flight. The
    // null→value transition also remounts the directive so its entrance animation
    // re-fires on every scan (not just the first). Mirrors CatcherPage / BinDirective.
    setDirective(null);
    try {
      const res = await stowMutation.mutateAsync({
        garmentUnitId: garmentId,
        locationId: selectedLocation.id,
      });
      setDirective({
        slotLabel: res.slotLabel,
        storageType: res.storageType,
        orderName: res.orderName,
        orderNumber: res.orderNumber,
        alreadyStored: res.alreadyStored,
      });
      // Reflect the new film in the occupancy grid.
      void utils.filmStorage.getFilmStorage.invalidate();
      // QRScannerInput already played the success chime on capture — do NOT beep
      // again (double-chime; useScanFeedback doc). The directive card is the
      // visual twin.
    } catch (error) {
      // No slot / all full / no film → operator-blocking modal with audio.
      setDirective(null);
      feedback.triggerError(t('filmStorage.stowError'), getUserMessage(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- feedback recreated every render
  }, [selectedLocation?.id, stowMutation, utils.filmStorage, t]);

  // Group pigeon holes by their bound order; shelves + legacy render separately.
  const { orderGroups, availableHoles, hasHoles, shelvesAndLegacy } = useMemo(() => {
    const slots = (storageQuery.data?.storage ?? []) as FilmSlot[];
    const holes = slots.filter((s) => s.storageType === 'pigeon_hole');
    const others = slots.filter((s) => s.storageType !== 'pigeon_hole');

    const byOrder = new Map<
      string,
      { orderId: string; orderName: string | null; orderNumber: string | null; holes: FilmSlot[] }
    >();
    const avail: FilmSlot[] = [];
    for (const h of holes) {
      if (h.currentOrderId) {
        const g = byOrder.get(h.currentOrderId) ?? {
          orderId: h.currentOrderId,
          orderName: h.currentOrderName,
          orderNumber: h.currentOrderNumber,
          holes: [],
        };
        g.holes.push(h);
        byOrder.set(h.currentOrderId, g);
      } else {
        avail.push(h);
      }
    }
    const natural = (a: string, b: string) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    for (const g of byOrder.values()) g.holes.sort((a, b) => natural(a.label, b.label));
    avail.sort((a, b) => natural(a.label, b.label));
    const groups = Array.from(byOrder.values()).sort((a, b) =>
      natural(a.orderNumber ?? a.orderName ?? '', b.orderNumber ?? b.orderName ?? ''),
    );
    return { orderGroups: groups, availableHoles: avail, hasHoles: holes.length > 0, shelvesAndLegacy: others };
  }, [storageQuery.data?.storage]);

  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">{t('filmStorage.selectLocation')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const occ = storageQuery.data?.occupancy;
  const slots = storageQuery.data?.storage ?? [];
  const selection: SlotSelection = editMode ? { selectedIds, onToggle: toggleSelect } : null;

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter">
      {/* ARIA live region */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {storageQuery.isLoading && t('lookingUp')}
        {directive && (directive.alreadyStored
          ? t('filmStorage.alreadyFiledInSlot', { slot: directive.slotLabel })
          : t('filmStorage.fileInSlot', { slot: directive.slotLabel }))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/10">
            <Archive className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('filmStorage.title')}</h1>
            <p className="text-muted-foreground">{t('filmStorage.description')}</p>
          </div>
        </div>
        {/* Setup/admin actions — secondary weight so the scan flow stays the
            page's visual focus. In edit mode the header collapses to just Done;
            the FloatingSelectionBar carries remove/clear. */}
        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          {editMode ? (
            <Button variant="secondary" onClick={exitEditMode} className="gap-2">
              <Check className="h-4 w-4" /> {t('filmStorage.doneEditing')}
            </Button>
          ) : (
            <>
              {canManageStorage && (
                <QRPreviewDialog
                  qrCodes={slotQRQuery.data ?? []}
                  binType="FILM_SLOT"
                  loading={slotQRQuery.isFetching}
                  open={showPrintLabels}
                  onOpenChange={setShowPrintLabels}
                >
                  <Button variant="outline" className="gap-2" disabled={slots.length === 0}>
                    <Printer className="h-4 w-4" /> {t('filmStorage.printLabels')}
                  </Button>
                </QRPreviewDialog>
              )}
              {isAdmin() && (
                <Button variant="outline" onClick={() => setEditMode(true)} className="gap-2" disabled={slots.length === 0}>
                  <Pencil className="h-4 w-4" /> {t('filmStorage.editStorage')}
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowCreate(true)} className="gap-2">
                <Plus className="h-4 w-4" /> {t('filmStorage.create')}
              </Button>
            </>
          )}
        </div>
      </div>

      {occ && !editMode && (
        <p className="text-sm text-muted-foreground tabular-nums">
          {t('filmStorage.occupancy', { inUse: occ.inUse + occ.full, available: occ.available, films: occ.filmsStored })}
        </p>
      )}

      {/* Edit-mode hint — colour ALWAYS paired with text (PRODUCTION_DESIGN §13). */}
      {editMode && (
        <div className="rounded-lg border border-dashed bg-muted/30 px-4 py-3 text-sm flex items-center gap-2">
          <Pencil className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
          <span>{t('filmStorage.editHint')}</span>
        </div>
      )}

      {/* Directed stow — scan a cut transfer, get told which slot to file it in
          (borrows the catcher's BinDirective put-away pattern). The one scanner
          also opens inspect on a THP://HOLE: scan (routed in handleScan). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ScanLine className="h-5 w-5" /> {t('filmStorage.stowTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <p className="text-sm text-muted-foreground">
            {slots.length === 0 ? t('filmStorage.stowNeedsStorage') : t('filmStorage.stowHint')}
          </p>
          <QRScannerInput
            onScan={handleScan}
            disabled={scannerBlocked || slots.length === 0}
            isLoading={stowMutation.isPending}
          />
          {directive && (
            <StowDirective directive={directive} />
          )}
        </CardContent>
      </Card>

      {/* Occupancy — loading / empty guards, then holes-by-order + shelves. */}
      {storageQuery.isLoading ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('filmStorage.slotsHeading')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {Array.from({ length: 12 }).map((_, i) => <Skeleton key={i} className="aspect-square rounded-2xl" />)}
            </div>
          </CardContent>
        </Card>
      ) : slots.length === 0 ? (
        <Card>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <FolderOpen className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">{t('filmStorage.empty')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Pigeon holes — clustered by order, then a trailing Available group. */}
          {hasHoles && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Inbox className="h-4 w-4" /> {t('filmStorage.pigeonHolesHeading')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {orderGroups.map((g) => (
                  <OrderCluster key={g.orderId} group={g} onInspect={openInspect} selection={selection} />
                ))}
                {availableHoles.length > 0 && (
                  <AvailableCluster holes={availableHoles} onInspect={openInspect} selection={selection} />
                )}
              </CardContent>
            </Card>
          )}

          {/* Shelves + legacy — classic square occupancy grid. */}
          {shelvesAndLegacy.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Layers className="h-4 w-4" /> {t('filmStorage.shelvesHeading')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {shelvesAndLegacy.map((s) => <FilmStorageCell key={s.id} slot={s} onInspect={openInspect} selection={selection} />)}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <CreateFilmStorageDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        locationId={selectedLocation.id}
        onCreated={() => { void utils.filmStorage.getFilmStorage.invalidate(); }}
      />

      <InspectDialog
        target={inspect}
        onClose={() => setInspect(null)}
        locationId={selectedLocation.id}
      />

      {/* Bulk-remove action bar (edit mode). Visible for the WHOLE edit session
          (not just when a slot is selected) so "Done" is always reachable even
          when the header scrolls off a long grid. Production skin: 48px targets
          + a station-appropriate pill (PRODUCTION_DESIGN §4.3). */}
      <FloatingSelectionBar
        visible={editMode}
        srStatus={t('filmStorage.selectedCount', { count: selectedIds.size })}
        ariaLabel={t('filmStorage.editStorage')}
        pillClassName="flex items-center gap-1.5 rounded-2xl border bg-card p-1.5 pl-3 shadow-lg"
      >
        {selectedIds.size > 0 && (
          <>
            <span className="px-1 text-base font-medium tabular-nums">
              {t('filmStorage.selectedCount', { count: selectedIds.size })}
            </span>
            <div className="mx-1 h-8 w-px bg-border" aria-hidden />
            <Button variant="ghost" className="h-12" onClick={() => setSelectedIds(new Set())}>
              {t('filmStorage.clearSelection')}
            </Button>
            <Button variant="destructive" className="h-12 gap-1.5" onClick={() => setShowRemoveConfirm(true)}>
              <Trash2 className="h-5 w-5" aria-hidden /> {t('filmStorage.removeSelected')}
            </Button>
            <div className="mx-1 h-8 w-px bg-border" aria-hidden />
          </>
        )}
        <Button variant="secondary" className="h-12 gap-1.5" onClick={exitEditMode}>
          <Check className="h-5 w-5" aria-hidden /> {t('filmStorage.doneEditing')}
        </Button>
      </FloatingSelectionBar>

      {/* Remove confirmation (edit mode is not a scan flow, so this doesn't
          contend with the scan-feedback modal — the scanner is paused). */}
      <Dialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('filmStorage.removeConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('filmStorage.removeConfirmDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRemoveConfirm(false)}>{t('common:cancel')}</Button>
            <Button variant="destructive" onClick={handleRemove} disabled={deleteMutation.isPending} className="gap-2">
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {t('filmStorage.removeConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScanErrorModal {...feedback.modalProps} />
    </div>
  );
}

// ============================================================================
// StowDirective — delegates to the shared FilmLocationDirective.
// ============================================================================

/**
 * The directed put-away card. Delegates entirely to the shared
 * FilmLocationDirective (no competing variant): a fresh stow shows
 * "FILE IN HOLE-07 → ORDER #1234"; an idempotent re-scan shows the informational
 * "already filed" treatment. The page owns the aria-live announcement.
 */
function StowDirective({ directive }: { directive: StowDirectiveState }) {
  return (
    <FilmLocationDirective
      slotLabel={directive.slotLabel}
      storageType={directive.storageType}
      orderName={directive.orderName}
      orderNumber={directive.orderNumber}
      action={directive.alreadyStored ? 'stowed' : 'stow'}
    />
  );
}

// ============================================================================
// Status styling shared by hole + shelf cells (colour ALWAYS paired with text).
// ============================================================================

const SLOT_STATUS_BORDER: Record<SlotVisual, string> = {
  OCCUPIED: 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30',
  EMPTY: 'border-border bg-muted/40',
  DEFECTIVE: 'border-red-400 bg-red-50 dark:bg-red-950/30',
};
const SLOT_STATUS_TEXT: Record<SlotVisual, string> = {
  OCCUPIED: 'text-emerald-700 dark:text-emerald-300',
  EMPTY: 'text-muted-foreground',
  DEFECTIVE: 'text-red-700 dark:text-red-300',
};
/** Soft circular accent tint for the pigeon-hole round motif. */
const SLOT_STATUS_CIRCLE: Record<SlotVisual, string> = {
  OCCUPIED: 'bg-emerald-500/10',
  EMPTY: 'bg-foreground/[0.03]',
  DEFECTIVE: 'bg-red-500/10',
};

function useStatusLabels() {
  const { t } = useTranslation('production');
  return useMemo<Record<SlotVisual, string>>(() => ({
    OCCUPIED: t('filmStorage.statusOccupied'),
    EMPTY: t('filmStorage.statusEmpty'),
    DEFECTIVE: t('filmStorage.statusDefective'),
  }), [t]);
}

// ============================================================================
// OrderCluster / AvailableCluster — pigeon holes grouped by bound order.
// ============================================================================

const HOLE_GRID = 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3';

function OrderCluster({
  group,
  onInspect,
  selection,
}: {
  group: { orderId: string; orderName: string | null; orderNumber: string | null; holes: FilmSlot[] };
  onInspect: (slot: FilmSlot) => void;
  selection: SlotSelection;
}) {
  const orderLabel = group.orderName || (group.orderNumber ? `#${group.orderNumber}` : group.orderId);
  return (
    <div className="rounded-xl border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Package className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
        <span className="font-mono text-sm font-semibold tabular-nums truncate">{orderLabel}</span>
        <Badge variant="secondary" className="ml-auto text-xs tabular-nums shrink-0">{group.holes.length}</Badge>
      </div>
      <div className={HOLE_GRID}>
        {group.holes.map((h) => <PigeonHoleCell key={h.id} slot={h} onInspect={onInspect} selection={selection} />)}
      </div>
    </div>
  );
}

function AvailableCluster({ holes, onInspect, selection }: { holes: FilmSlot[]; onInspect: (slot: FilmSlot) => void; selection: SlotSelection }) {
  const { t } = useTranslation('production');
  return (
    <div className="rounded-xl border border-dashed p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Inbox className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
        <span className="text-sm font-medium text-muted-foreground">{t('filmStorage.availableHoles')}</span>
        <Badge variant="secondary" className="ml-auto text-xs tabular-nums shrink-0">{holes.length}</Badge>
      </div>
      <div className={HOLE_GRID}>
        {holes.map((h) => <PigeonHoleCell key={h.id} slot={h} onInspect={onInspect} selection={selection} />)}
      </div>
    </div>
  );
}

// ============================================================================
// PigeonHoleCell — round-motif, order-bound cell (tap to inspect).
// ============================================================================

/**
 * A pigeon hole reads as ROUND (a rounded-rect with a soft circular inner
 * accent) — the round motif signals "one film per hole" at a glance — but stays
 * a square box so it can legibly hold the label, order #, count AND the status
 * TEXT (colour-only status is forbidden, PRODUCTION_DESIGN §13). A bare
 * rounded-full inscribed circle can't. Tap opens the full-screen inspect.
 */
function PigeonHoleCell({ slot, onInspect, selection }: { slot: FilmSlot; onInspect: (slot: FilmSlot) => void; selection: SlotSelection }) {
  const statusLabels = useStatusLabels();
  const visual = slotVisual(slot);
  const order = slot.currentOrderNumber
    ? `#${slot.currentOrderNumber}`
    : slot.currentOrderName || null;
  const selectMode = !!selection;
  const selected = selectMode && selection.selectedIds.has(slot.id);
  return (
    <button
      type="button"
      onClick={() => (selectMode ? selection.onToggle(slot.id) : onInspect(slot))}
      aria-pressed={selectMode ? selected : undefined}
      className={cn(
        'relative aspect-square rounded-2xl border-2 p-2 flex flex-col items-center justify-center gap-0.5 text-center overflow-hidden transition-all',
        'hover:ring-2 hover:ring-primary hover:ring-offset-1 cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
        SLOT_STATUS_BORDER[visual],
        selected && 'ring-2 ring-primary ring-offset-1',
      )}
      title={`${slot.label}${order ? `: ${order}` : ''}`}
    >
      {/* Selection indicator (edit mode) — icon, not colour alone (§13). */}
      {selectMode && (
        <span className="absolute top-1 right-1 z-20" aria-hidden>
          {selected
            ? <CheckCircle2 className="h-5 w-5 text-primary" />
            : <Circle className="h-5 w-5 text-muted-foreground/40" />}
        </span>
      )}
      {/* Soft circular inner accent — decorative round motif behind the content. */}
      <span
        aria-hidden
        className={cn('pointer-events-none absolute inset-[16%] rounded-full', SLOT_STATUS_CIRCLE[visual])}
      />
      <span className="relative z-10 font-mono text-sm font-semibold leading-tight truncate max-w-full">{slot.label}</span>
      {order && (
        <span className="relative z-10 text-sm font-semibold leading-tight tabular-nums truncate max-w-full">{order}</span>
      )}
      <span className="relative z-10 text-[11px] text-muted-foreground tabular-nums">
        {slot.capacity != null
          ? `${slot.currentCount} / ${slot.capacity}`
          : `${slot.currentCount}`}
      </span>
      <span className={cn('relative z-10 text-xs font-medium uppercase tracking-wide', SLOT_STATUS_TEXT[visual])}>
        {statusLabels[visual]}
      </span>
    </button>
  );
}

// ============================================================================
// FilmStorageCell — one shelf / legacy slot, square (tap to inspect).
// ============================================================================

function FilmStorageCell({ slot, onInspect, selection }: { slot: FilmSlot; onInspect: (slot: FilmSlot) => void; selection: SlotSelection }) {
  const { t } = useTranslation('production');
  const statusLabels = useStatusLabels();
  const visual = slotVisual(slot);
  const selectMode = !!selection;
  const selected = selectMode && selection.selectedIds.has(slot.id);
  return (
    <button
      type="button"
      onClick={() => (selectMode ? selection.onToggle(slot.id) : onInspect(slot))}
      aria-pressed={selectMode ? selected : undefined}
      className={cn(
        'relative rounded-xl border-2 p-3 flex flex-col gap-1 min-h-24 text-left transition-all',
        'hover:ring-2 hover:ring-primary hover:ring-offset-1 cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
        SLOT_STATUS_BORDER[visual],
        selected && 'ring-2 ring-primary ring-offset-1',
      )}
      title={slot.label}
    >
      {/* Selection indicator (edit mode) — icon, not colour alone (§13). */}
      {selectMode && (
        <span className="absolute top-1.5 right-1.5 z-20" aria-hidden>
          {selected
            ? <CheckCircle2 className="h-5 w-5 text-primary" />
            : <Circle className="h-5 w-5 text-muted-foreground/40" />}
        </span>
      )}
      <div className="flex items-center gap-1.5">
        <Layers className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
        {/* Operator-readable label — text-sm minimum (PRODUCTION_DESIGN §4.2). */}
        <span className="font-mono text-sm font-semibold truncate">{slot.label}</span>
      </div>
      <span className="text-sm text-muted-foreground tabular-nums">
        {slot.capacity != null
          ? t('filmStorage.slotCount', { count: slot.currentCount, capacity: slot.capacity })
          : t('filmStorage.slotCountUncapped', { count: slot.currentCount })}
      </span>
      <span className={cn('text-xs font-medium uppercase tracking-wide', SLOT_STATUS_TEXT[visual])}>
        {statusLabels[visual]}
      </span>
      {slot.designGroup && <Badge variant="secondary" className="text-sm w-fit">{slot.designGroup}</Badge>}
    </button>
  );
}

// ============================================================================
// InspectDialog — full-screen slot inspect (mirrors BinManagePage bin detail).
// ============================================================================

/** Semantic film-unit status styling — colour ALWAYS paired with the label text. */
const FILM_STATUS_STYLE: Record<string, string> = {
  PRINTING: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  CURING: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  CUT: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  STORED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  PRESSED: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200',
  DEFECTIVE: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

function InspectDialog({
  target,
  onClose,
  locationId,
}: {
  target: InspectTarget | null;
  onClose: () => void;
  locationId: string;
}) {
  const { t } = useTranslation('production');
  const open = target !== null;
  const utils = trpc.useUtils();
  // A DTF Printer cannot file a redo from here (its role is refused
  // catcher.createRedo); it redoes films on Film Printing instead.
  const { isDtfPrinter } = useRole();
  const canReportIssue = !isDtfPrinter();

  const detailsQuery = trpc.filmStorage.getPigeonHoleDetails.useQuery(
    target ? { locationId, storageId: target.storageId, label: target.label } : undefined!,
    { enabled: open && !!target && (!!target.storageId || !!target.label), staleTime: 5_000 },
  );

  const details = detailsQuery.data;
  const slot = details?.slot;
  const films = detailsQuery.data?.films ?? [];
  const order = slot?.currentOrderName || (slot?.currentOrderNumber ? `#${slot.currentOrderNumber}` : null);

  // Report issue → film-only redo: reuses catcher.createRedo(film_only) — the
  // canonical film-redo path (scraps the film, frees this slot, reverts the
  // garment, queues the redo). Same reasons as the film-print station.
  const redoMutation = trpc.catcher.createRedo.useMutation();
  const [reportGarmentId, setReportGarmentId] = useState<string | null>(null);
  const handleReportConfirm = useCallback(async (reason: string) => {
    if (!reportGarmentId) return;
    try {
      const result = await redoMutation.mutateAsync({
        garmentUnitId: reportGarmentId,
        locationId,
        reason,
        redoSubtype: 'film_only' as const,
      });
      if (result.success) {
        setReportGarmentId(null);
        playSuccessBeep();
        toast.success(t('filmStorage.reportIssueDone'), { duration: 3000 });
        void utils.filmStorage.getPigeonHoleDetails.invalidate();
        void utils.filmStorage.getFilmStorage.invalidate();
      }
    } catch (error) {
      setReportGarmentId(null);
      toast.error(t('filmStorage.reportIssueFailed'), { description: getUserMessage(error), duration: 5000 });
    }
  }, [reportGarmentId, locationId, redoMutation, utils, t]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono text-2xl tabular-nums">
            {slot?.label ?? t('filmStorage.inspectTitle')}
          </DialogTitle>
          <DialogDescription>{t('filmStorage.inspectDesc')}</DialogDescription>
        </DialogHeader>

        {detailsQuery.isLoading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
          </div>
        ) : detailsQuery.isError ? (
          <div className="py-12 text-center space-y-1">
            <p className="text-lg font-medium">{t('filmStorage.inspectError')}</p>
            <p className="text-sm text-muted-foreground">{getUserMessage(detailsQuery.error)}</p>
          </div>
        ) : !slot ? (
          <div className="py-12 text-center">
            <p className="text-lg font-medium text-muted-foreground">{t('filmStorage.inspectNotFound')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Retrieval directive — HOLE (order-bound) vs SHELF (design/size). */}
            <FilmLocationDirective
              slotLabel={slot.label}
              storageType={slot.storageType}
              orderName={slot.currentOrderName}
              orderNumber={slot.currentOrderNumber}
              designHint={slot.designGroup}
            />

            {films.length === 0 ? (
              <div className="py-10 text-center border rounded-xl border-dashed">
                <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-60" aria-hidden />
                <p className="text-lg font-medium">{t('filmStorage.emptyHole')}</p>
                <p className="text-sm text-muted-foreground">{t('filmStorage.emptyHoleDesc')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Package className="h-4 w-4 text-muted-foreground" aria-hidden />
                  {order ? (
                    <span>{t('filmDirective.forOrder', { order })}</span>
                  ) : (
                    <span>{t('filmStorage.filmsInSlot', { count: films.length })}</span>
                  )}
                  <Badge variant="secondary" className="ml-auto text-xs tabular-nums">{films.length}</Badge>
                </div>
                <div className="divide-y rounded-lg border">
                  {films.map((f) => {
                    const meta = [f.size, f.color].filter(Boolean).join(' · ');
                    return (
                      <div key={f.filmUnitId} className="flex items-center gap-3 p-3">
                        {/* Thumbnail */}
                        <div className="h-14 w-14 rounded-md bg-muted flex items-center justify-center overflow-hidden shrink-0">
                          {f.imageUrl ? (
                            <ZoomImg src={f.imageUrl} alt={f.productName ?? f.sku ?? ''} className="h-full w-full object-cover" loading="lazy" />
                          ) : (
                            <ImageOff className="h-5 w-5 text-muted-foreground" aria-hidden />
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-medium truncate">{f.productName || f.shopifySku || f.sku || t('filmStorage.unknownFilm')}</p>
                          {(f.shopifySku || f.sku) && (
                            <p className="text-sm text-muted-foreground font-mono truncate">{f.shopifySku || f.sku}</p>
                          )}
                          {meta && <p className="text-sm text-muted-foreground truncate">{meta}</p>}
                        </div>
                        {/* Film status — colour + text */}
                        <Badge className={cn('text-xs shrink-0', FILM_STATUS_STYLE[f.status] ?? 'bg-muted text-muted-foreground')}>
                          {f.status.replace(/_/g, ' ')}
                        </Badge>
                        {/* Report issue → film-only redo (not for already-
                            pressed films — the server rejects those). */}
                        {canReportIssue && f.garmentUnitId && f.status !== 'PRESSED' && f.status !== 'DEFECTIVE' && (
                          <Button
                            variant="outline"
                            className="h-12 shrink-0"
                            onClick={() => setReportGarmentId(f.garmentUnitId)}
                            disabled={redoMutation.isPending}
                          >
                            {t('filmStorage.reportIssue')}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      {/* Report-issue confirm (blocking modal §6.1) — film-failure reasons. */}
      <FilmRedoDialog
        open={reportGarmentId !== null}
        onOpenChange={(o) => { if (!o) setReportGarmentId(null); }}
        onConfirm={handleReportConfirm}
        isPending={redoMutation.isPending}
      />
    </Dialog>
  );
}

// ============================================================================
// CreateFilmStorageDialog
// ============================================================================

function CreateFilmStorageDialog({
  open, onOpenChange, locationId, onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  locationId: string;
  onCreated: () => void;
}) {
  const { t } = useTranslation('production');
  const [count, setCount] = useState('5');
  const [storageType, setStorageType] = useState<StorageType>('pigeon_hole');
  const [capacity, setCapacity] = useState('');
  const [designGroup, setDesignGroup] = useState('');

  const isHole = storageType === 'pigeon_hole';

  const createMutation = trpc.filmStorage.createFilmStorage.useMutation();

  const handleCreate = useCallback(async () => {
    const n = parseInt(count, 10);
    if (!Number.isFinite(n) || n < 1) return;
    try {
      const res = await createMutation.mutateAsync({
        locationId,
        count: n,
        storageType,
        // Pigeon holes are capacity=1 (forced server-side) and order-bound, so
        // capacity + design group are shelf-only concepts.
        capacity: !isHole && capacity ? parseInt(capacity, 10) : null,
        designGroup: !isHole && designGroup.trim() ? designGroup.trim() : null,
      });
      toast.success(t('filmStorage.createConfirm'), { description: `${res.created.length}`, duration: 2500 });
      onCreated();
      onOpenChange(false);
      setCount('5'); setStorageType('pigeon_hole'); setCapacity(''); setDesignGroup('');
    } catch (error) {
      toast.error(t('filmStorage.createFailed'), { description: getUserMessage(error) });
    }
  }, [count, storageType, isHole, capacity, designGroup, locationId, createMutation, onCreated, onOpenChange, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('filmStorage.createTitle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Type first — it drives which fields below are relevant. */}
          <div className="space-y-1.5">
            <Label>{t('filmStorage.type')}</Label>
            <Select value={storageType} onValueChange={(v) => setStorageType(v as StorageType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pigeon_hole">{t('filmStorage.typePigeonHole')}</SelectItem>
                <SelectItem value="shelf">{t('filmStorage.typeShelf')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fs-count">{isHole ? t('filmStorage.numberOfHoles') : t('filmStorage.count')}</Label>
            <Input id="fs-count" type="number" min={1} max={100} value={count} onChange={(e) => setCount(e.target.value)} />
            {isHole && <p className="text-xs text-muted-foreground">{t('filmStorage.oneFilmPerHole')}</p>}
          </div>
          {/* Capacity + design group are shelf-only. */}
          {!isHole && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="fs-cap">{t('filmStorage.capacity')}</Label>
                <Input id="fs-cap" type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fs-dg">{t('filmStorage.designGroup')}</Label>
                <Input id="fs-dg" value={designGroup} onChange={(e) => setDesignGroup(e.target.value)} maxLength={100} />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common:cancel')}</Button>
          <Button onClick={handleCreate} disabled={createMutation.isPending} className="gap-2">
            {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('filmStorage.createConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
